import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../../services/api";
import { recruitingAPI } from "../../services/recruitingApi";

export interface Candidate {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  location?: string;
  linkedin_url?: string;
  main_skill?: string;
  seniority?: string;
  role_type?: string;
  expected_rate?: number;
  notice_period?: string;
  english_level?: string;
  decision?: string;
  source?: string;
  summary?: string;
  resume_url?: string;
  tags?: string[];
  candidate_code?: string;
  status?: string;
  created_at: string;
  updated_at: string;
}

export interface Application {
  id: number;
  candidate_id: number;
  job_id: number;
  job_title: string;
  job_location?: string;
  expected_rate?: number;
  status: string;
  ai_match_score?: number;
  status_changed_at?: string;
  rejection_reason?: string;
  created_at: string;
}

export interface Job {
  id: number;
  title: string;
  department?: string;
  location?: string;
  seniority?: string;
  employment_type?: string;
  skills?: string[];
  description?: string;
}

export interface GdprStatusData {
  status: "none" | "pending" | "given" | "withdrawn" | "deleted";
  isGrandfathered: boolean;
}

interface GdprStatusResponse {
  success: boolean;
  data?: GdprStatusData;
}

interface UseCandidateDataOptions {
  candidateId: string | undefined;
  enabled?: boolean;
}

interface UseCandidateDataReturn {
  // Candidate data
  candidate: Candidate | undefined;
  candidateLoading: boolean;
  candidateError: unknown;

  // Applications
  applications: Application[];
  applicationsLoading: boolean;

  // GDPR Status
  gdprStatusData: GdprStatusData | undefined;

  // Jobs (for tech check and assessment)
  allJobs: Job[];
  jobsForAssessment: Job[];
  jobsLoading: boolean;

  // History - typed as unknown[] to let the consuming component handle the type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any[];
  historyLoading: boolean;
  historyError: unknown;

  // AI Summary generation
  generateSummary: () => void;
  isGeneratingSummary: boolean;
}

/**
 * Custom hook for fetching all candidate-related data
 *
 * @example
 * const {
 *   candidate,
 *   candidateLoading,
 *   candidateError,
 *   applications,
 *   gdprStatusData,
 *   allJobs,
 *   generateSummary,
 * } = useCandidateData({ candidateId: id });
 */
export function useCandidateData({
  candidateId,
  enabled = true,
}: UseCandidateDataOptions): UseCandidateDataReturn {
  const queryClient = useQueryClient();
  const numericId = candidateId ? Number.parseInt(candidateId, 10) : 0;
  const isEnabled = enabled && !!candidateId;

  // Fetch candidate details
  const {
    data: candidateData,
    isLoading: candidateLoading,
    error: candidateError,
  } = useQuery(
    ["candidate", candidateId],
    () => api.recruiting.getCandidate(numericId),
    {
      enabled: isEnabled,
      onError: (error: unknown) => {
        console.error("Error fetching candidate:", error);
      },
    },
  );

  // Fetch candidate applications
  const { data: applicationsData, isLoading: applicationsLoading } = useQuery(
    ["candidate-applications", candidateId],
    () => api.recruiting.getApplications({ candidate_id: candidateId }),
    {
      enabled: isEnabled,
      onError: (error: unknown) => {
        console.error("Error fetching candidate applications:", error);
      },
    },
  );

  // Fetch GDPR status for header indicator
  const { data: gdprResponse } = useQuery<GdprStatusResponse>(
    ["candidate-gdpr-status", candidateId],
    () =>
      recruitingAPI.getCandidateGdpr(numericId) as Promise<GdprStatusResponse>,
    {
      enabled: isEnabled,
      staleTime: 30 * 1000,
      onError: (error: unknown) => {
        console.error("Error fetching GDPR status:", error);
      },
    },
  );

  // Fetch jobs for tech check and assessment (single query for both use cases)
  const { data: allJobsData, isLoading: jobsLoading } = useQuery(
    "jobs",
    () => api.recruiting.getJobs(),
    {
      staleTime: 5 * 60 * 1000,
      onError: (error: unknown) => {
        console.error("Error fetching jobs:", error);
      },
    },
  );

  // Fetch AI artifacts placeholder
  useQuery(
    ["candidate-ai-artifacts", candidateId],
    async () => ({ data: [] }),
    {
      enabled: isEnabled,
      onError: (error: unknown) => {
        console.error("Error fetching AI artifacts:", error);
      },
    },
  );

  // Fetch candidate history
  const {
    data: historyData,
    isLoading: historyLoading,
    error: historyError,
  } = useQuery(
    ["candidate-history", candidateId],
    () => api.recruiting.getCandidateHistory(numericId),
    {
      enabled: isEnabled,
      onError: (error: unknown) => {
        console.error("Error fetching candidate history:", error);
      },
    },
  );

  // Generate AI summary mutation
  const generateSummaryMutation = useMutation(
    () => api.recruiting.summarizeCandidate(numericId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["candidate", candidateId]);
        queryClient.invalidateQueries(["candidate-ai-artifacts", candidateId]);
      },
      onError: (error: unknown) => {
        console.error("Error generating AI summary:", error);
      },
    },
  );

  return {
    // Candidate
    candidate: candidateData?.data as Candidate | undefined,
    candidateLoading,
    candidateError,

    // Applications
    applications: (applicationsData ?? []) as Application[],
    applicationsLoading,

    // GDPR
    gdprStatusData: gdprResponse?.data,

    // Jobs (using single query for both use cases to avoid duplicate API calls)
    allJobs: (allJobsData?.data || []) as Job[],
    jobsForAssessment: (allJobsData?.data || []) as Job[],
    jobsLoading,

    // History
    history: historyData?.data || [],
    historyLoading,
    historyError,

    // Summary generation
    generateSummary: () => generateSummaryMutation.mutate(),
    isGeneratingSummary: generateSummaryMutation.isLoading,
  };
}

export default useCandidateData;
